import React, { Fragment, useContext } from "react";
import SelectableTable from "components/DesignSystem/SelectableTable";
import { Link, useHistory, useLocation } from "react-router-dom";
import { packClasses } from "Utils";
import { AssignmentContext } from "context/Assignment";
import { range } from "lodash";
import EmptyState from "components/DesignSystem/EmptyState";
import { fromSqlDateTime, SQL_DATE_FORMAT } from "helpers/Date";
import { LockOpenIcon, LockClosedIcon } from "@heroicons/react/outline";
import Pill from "components/Text/Pill";

export default function AssignmentTable(props) {
    const { items, selectedItems, loading } = useContext(AssignmentContext);
    const history = useHistory();
    const location = useLocation();

    const selectAll = (e) => {
        if (e.target.checked) {
            props.setAllSelected(items.map((item) => item.uuid));
        } else {
            props.setAllSelected([]);
        }
    };

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && params.get(key) === val) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onSort = (sortKey) => {
        let sortDir = "desc";
        if (
            props.queryParams.has("order") &&
            props.queryParams.get("order") === sortKey
        ) {
            if (
                props.queryParams.has("dir") &&
                props.queryParams.get("dir") === "asc"
            ) {
                sortDir = "desc";
            } else {
                sortDir = "asc";
            }
        }

        updateParams({ order: sortKey, dir: sortDir });
    };

    return (
        <SelectableTable total={items.length} selected={selectedItems.length}>
            {({ innerRef }) => (
                <Fragment>
                    <thead className="bg-gray-50 ">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <input
                                    type="checkbox"
                                    className="appearance-none rounded text-green-700 focus:ring-green-700"
                                    ref={innerRef}
                                    onChange={(e) => selectAll(e)}
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <button
                                    type="button"
                                    className={packClasses(
                                        "text-xs uppercase tracking-wider",
                                        props.queryParams.has("order") &&
                                            props.queryParams.get("order") ===
                                                "title"
                                            ? "font-bold text-gray-800"
                                            : "font-medium"
                                    )}
                                    onClick={() => onSort("title")}
                                >
                                    Title
                                </button>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <button
                                    type="button"
                                    className={packClasses(
                                        "text-xs uppercase tracking-wider",
                                        props.queryParams.has("order") &&
                                            props.queryParams.get("order") ===
                                                "action_start_time"
                                            ? "font-bold text-gray-800"
                                            : "font-medium"
                                    )}
                                    onClick={() => onSort("action_start_time")}
                                >
                                    Event Start
                                </button>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <button
                                    type="button"
                                    className={packClasses(
                                        "text-xs uppercase tracking-wider",
                                        !props.queryParams.has("order") ||
                                            props.queryParams.get("order") ===
                                                "due_at"
                                            ? "font-bold text-gray-800"
                                            : "font-medium"
                                    )}
                                    onClick={() => onSort("due_at")}
                                >
                                    Due
                                </button>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <button
                                    type="button"
                                    className={packClasses(
                                        "text-xs uppercase tracking-wider",
                                        props.queryParams.has("order") &&
                                            props.queryParams.get("order") ===
                                                "issue_id"
                                            ? "font-bold text-gray-800"
                                            : "font-medium"
                                    )}
                                    onClick={() => onSort("issue_id")}
                                >
                                    Issue
                                </button>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                <button
                                    type="button"
                                    className={packClasses(
                                        "text-xs uppercase tracking-wider",
                                        props.queryParams.has("order") &&
                                            props.queryParams.get("order") ===
                                                "modified_at"
                                            ? "font-bold text-gray-800"
                                            : "font-medium"
                                    )}
                                    onClick={() => onSort("modified_at")}
                                >
                                    Last Modified
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {!loading && items.length === 0 && (
                            <tr>
                                <td colSpan={5}>
                                    <EmptyState />
                                </td>
                            </tr>
                        )}
                        {loading
                            ? range(0, 10).map((i) => (
                                  <tr className="animate-pulse" key={i}>
                                      <td
                                          className="whitespace-nowrap px-4 py-2"
                                          colSpan={6}
                                      >
                                          <div className="my-1 h-5 rounded bg-gray-300 leading-none">
                                              &nbsp;
                                          </div>
                                      </td>
                                  </tr>
                              ))
                            : items.map((item) => (
                                  <tr key={item.id}>
                                      <td className="whitespace-nowrap px-6 py-2">
                                          <input
                                              className="appearance-none rounded text-green-700 focus:ring-green-700"
                                              type="checkbox"
                                              onChange={(e) => {
                                                  props.addSelected(
                                                      e,
                                                      item.uuid
                                                  );
                                              }}
                                              checked={selectedItems.find(
                                                  (id) => id === item.uuid
                                              )}
                                          />
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-2">
                                          <Link
                                              to={`/app/planner/assignment/${item.uuid}`}
                                              className="color-gray-800 hover:underline"
                                          >
                                              <span className="pr-2">
                                                  {item.title}
                                              </span>
                                          </Link>
                                          {item.lock && item.lock.user ? (
                                              <Pill
                                                  className={packClasses(
                                                      "border border-transparent bg-gray-100 text-red-700"
                                                  )}
                                              >
                                                  <LockClosedIcon className=" mr-1 h-2.5 w-2.5" />{" "}
                                                  {item.lock.user.name}
                                              </Pill>
                                          ) : (
                                              <Pill>
                                                  <LockOpenIcon className="h-2.5 w-2.5" />
                                              </Pill>
                                          )}
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-2">
                                          <Link
                                              to={`/app/planner/assignment/${item.uuid}`}
                                              className="color-gray-600 text-sm hover:underline"
                                          >
                                              {item.action_start_time ? (
                                                  fromSqlDateTime(
                                                      item.action_start_time
                                                  ).toFormat(
                                                      SQL_DATE_FORMAT.concat(
                                                          " t"
                                                      )
                                                  )
                                              ) : (
                                                  <span className="text-gray-400">
                                                      None
                                                  </span>
                                              )}
                                          </Link>
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-2">
                                          <Link
                                              to={`/app/planner/assignment/${item.uuid}`}
                                              className="color-gray-600 text-sm hover:underline"
                                          >
                                              {item.due_at ? (
                                                  fromSqlDateTime(
                                                      item.due_at
                                                  ).toFormat(SQL_DATE_FORMAT)
                                              ) : (
                                                  <span className="text-gray-400">
                                                      None
                                                  </span>
                                              )}
                                          </Link>
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-2 text-sm">
                                          {item.issue_id ? (
                                              <span>
                                                  {item.issue.decorated_label}
                                              </span>
                                          ) : (
                                              <span className="text-gray-400">
                                                  None
                                              </span>
                                          )}
                                      </td>
                                      <td className="whitespace-nowrap px-6 py-2">
                                          <Link
                                              to={`/app/planner/assignment/${item.uuid}`}
                                              className="color-gray-600 text-sm hover:underline"
                                          >
                                              {item.modified_at ? (
                                                  fromSqlDateTime(
                                                      item.modified_at
                                                  ).toFormat(
                                                      SQL_DATE_FORMAT.concat(
                                                          " t"
                                                      )
                                                  )
                                              ) : (
                                                  <span className="text-gray-400">
                                                      None
                                                  </span>
                                              )}
                                          </Link>
                                      </td>
                                  </tr>
                              ))}
                    </tbody>
                </Fragment>
            )}
        </SelectableTable>
    );
}
